import Immutable from 'seamless-immutable';
import _ from 'lodash';
import RefData from 'ref-data';

const regexPattern =
  /^(?:\"(.+)\"\s)*(?:<)*([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+)@([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*)*(?:>)*$/;

/**
 * Email Integration
 * @typedef {object} Email Integration
 *
 * @property {string} blueprint
 * @property {string} classRef            RefData.Enum.CLASS.INTEGRATION.EMAIL
 * @property {string} containerRef
 * @property {string} eventType           RefData.Enum.EVENT.INTEGRATION.ENTRY, RefData.Enum.EVENT.INTEGRATION.PUBLISHENTRY
 * @property {string} help
 * @property {string} icon
 * @property {string} id
 * @property {object} instanceConfig
 * @property {string} body
 * @property {string} from
 * @property {string} replyTo
 * @property {string} subject
 * @property {string} to
 * @property {string} type
 * @property {string} lastUpdated
 * @property {string} modifiedBy
 * @property {string} name
 * @property {string} status          RefData.Enum.STATUS.INTEGRATION.ACTIVE, RefData.Enum.STATUS.INTEGRATION.INACTIVE
 *
 */

function create(emailIntegration) {
  const result = _.clone(emailIntegration);

  result.instanceConfig = result.instanceConfig ? result.instanceConfig : {};

  if (!_.isEmpty(result)) {
    result.classRef = RefData.Model.hydrate(emailIntegration.classRef);
    result.eventType = RefData.Model.hydrate(emailIntegration.eventType);
    result.status = RefData.Model.hydrate(emailIntegration.status);
  }

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend emailIntegration object into a frontend EmailIntegration Model
 * @param {Object} emailIntegration - backend formatted email integration
 * @returns {EmailIntegration}
 */
function deserialize(emailIntegration) {
  const _result = _.pick(emailIntegration, _keys);

  //we separate the string ' "Friendlynname" <email@eamil.com> ' into
  //0: '"Friendlynname" <email@eamil.com>' - full address
  //1: '"Friendlynname"'
  //2: 'email'
  //3: 'email.com'
  const [, _replyToFriendly, _replyToAddress, _replyToDomain] = regexPattern.exec(_result.instanceConfig.replyTo) || [];
  const [, _fromFriendly, _fromAddress, _fromDomain] = regexPattern.exec(_result.instanceConfig.from) || [];

  const result = {
    ..._result,
    instanceConfig: {
      ..._result.instanceConfig,
      _fromDomain,
      _fromAddress,
      _fromFriendly,
      _replyTo: `${_replyToAddress || ''}${
        (_result.instanceConfig.replyTo && _result.instanceConfig.replyTo.indexOf('@') === -1) ||
        !_result.instanceConfig.replyTo
          ? ''
          : '@'
      }${_replyToDomain || ''}`,
      _replyToFriendly: _replyToFriendly || '',
    },
  };

  return create(result);
}
/**
 * Transform a EmailIntegrationModel into a backend emailIntegration object
 * @returns {Object}
 */
function serialize(emailIntegration) {
  const result = emailIntegration.asMutable({ deep: true });

  let from = '';
  if (result.instanceConfig._fromFriendly) {
    from = `"${result.instanceConfig._fromFriendly}" <${[
      result.instanceConfig._fromAddress,
      result.instanceConfig._fromDomain,
    ]
      .join('@')
      .replace(/\s+/g, '')}>`;
  } else {
    from = `${[result.instanceConfig._fromAddress, result.instanceConfig._fromDomain].join('@')}`.replace(/\s+/g, '');
  }

  let replyTo;
  if (result.instanceConfig._replyToFriendly) {
    replyTo = `"${result.instanceConfig._replyToFriendly}" <${result.instanceConfig._replyTo.replace(/\s+/g, '')}>`;
  } else {
    replyTo = result.instanceConfig._replyTo.replace(/\s+/g, '');
  }

  delete result.instanceConfig._fromAddress;
  delete result.instanceConfig._fromDomain;

  delete result.instanceConfig._fromFriendly;
  delete result.instanceConfig._replyToFriendly;
  delete result.instanceConfig._replyTo;

  result.classRef = emailIntegration.classRef.id;
  result.eventType = emailIntegration.eventType.id;
  result.status = emailIntegration.status.id;

  const _result = {
    ...result,
    instanceConfig: {
      ...result.instanceConfig,
      from,
      replyTo,
    },
  };

  return _.pick(_result, _keys);
}

const model = {};

const _keys = [
  'classRef',
  'containerRef',
  'eventType',
  'help',
  'icon',
  'id',
  'instanceConfig',
  'body',
  'from',
  'replyTo',
  'subject',
  'to',
  'type',
  'lastUpdated',
  'modifiedBy',
  'name',
  'status',
];

export default {
  create,
  deserialize,
  serialize,
};
